import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { HeartIcon, UsersIcon, VideoIcon, TrophyIcon, GiftIcon, ImageIcon, CalendarIcon, SparklesIcon, GlobeIcon, CheckIcon, ArrowRightIcon, MessageCircleIcon, MapPinIcon, StarIcon, XIcon, Sparkles, Camera, Bell, Share2Icon } from 'lucide-react';
export function DestaPass() {
  const [activeTab, setActiveTab] = useState<'couples' | 'guests'>('couples');
  const calendlyUrl = 'https://calendly.com/ashsingh/destapass-discovery-call';
  const features = [{
    icon: <UsersIcon className="h-8 w-8" data-id="element-826" />,
    title: 'Pre-Wedding Social Network',
    description: 'Connect both families months before the big day. Chat, share stories, and build relationships before you even meet in person.',
    color: 'from-purple-500 to-purple-600'
  }, {
    icon: <GlobeIcon className="h-8 w-8" data-id="element-827" />,
    title: 'Destination & Cultural Guide',
    description: 'Interactive guides about your destination and South Asian wedding traditions. Help every guest feel informed and included.',
    color: 'from-teal-500 to-teal-600'
  }, {
    icon: <Share2Icon className="h-8 w-8" data-id="element-828" />,
    title: 'Live Social Sharing',
    description: 'Your content creator and wedding attendees capture and share amazing live moments. Everyone becomes part of the storytelling experience.',
    color: 'from-pink-500 to-pink-600'
  }, {
    icon: <TrophyIcon className="h-8 w-8" data-id="element-829" />,
    title: 'Quests & Games',
    description: 'Fun challenges, scavenger hunts, and icebreaker activities. Turn strangers into friends through playful engagement.',
    color: 'from-amber-500 to-amber-600'
  }, {
    icon: <GiftIcon className="h-8 w-8" data-id="element-830" />,
    title: 'Exclusive Perks',
    description: 'Curated discounts on local tours, restaurants, and activities. VIP access to pre-wedding events and special experiences.',
    color: 'from-indigo-500 to-indigo-600'
  }, {
    icon: <Camera className="h-8 w-8" data-id="element-831" />,
    title: 'Shared Memories',
    description: 'Centralized photo and video sharing with AI tagging. No more scattered WhatsApp groups—one beautiful collaborative album.',
    color: 'from-rose-500 to-rose-600'
  }, {
    icon: <Bell className="h-8 w-8" data-id="element-832" />,
    title: 'Smart Logistics',
    description: 'Event schedules, transportation coordination, and real-time updates. Keep everyone informed and on time, effortlessly.',
    color: 'from-cyan-500 to-cyan-600'
  }];
  const timeline = [{
    phase: '3 Months Before',
    icon: <SparklesIcon className="h-6 w-6" data-id="element-833" />,
    activities: ['Families join and create profiles', 'Share engagement stories and photos', 'Start cultural education modules', 'Begin icebreaker challenges']
  }, {
    phase: '1 Month Before',
    icon: <MessageCircleIcon className="h-6 w-6" data-id="element-834" />,
    activities: ['Destination guides unlock', 'Group chats heat up', 'Quest activities ramp up', 'Travel coordination begins']
  }, {
    phase: 'Wedding Week',
    icon: <MapPinIcon className="h-6 w-6" data-id="element-835" />,
    activities: ['Live event schedules and updates', 'Real-time transportation coordination', 'Local perks and discounts activate', 'Scavenger hunts during events']
  }, {
    phase: 'During Events',
    icon: <VideoIcon className="h-6 w-6" data-id="element-836" />,
    activities: ['Live social sharing and updates', 'Real-time photo sharing', 'Virtual guest participation', 'Instant memory creation']
  }, {
    phase: 'After Wedding',
    icon: <HeartIcon className="h-6 w-6" data-id="element-837" />,
    activities: ['Collaborative album completion', 'Professional photo galleries', 'Recorded ceremony highlights', 'Continued family connections']
  }];
  const problems = [{
    problem: 'Scattered Communication',
    solution: 'One unified platform for all wedding communication'
  }, {
    problem: "Guests Don't Know Each Other",
    solution: 'Pre-wedding social network builds connections early'
  }, {
    problem: 'Remote Family Missing Out',
    solution: 'Live social sharing brings everyone into the celebration'
  }, {
    problem: 'Cultural Confusion',
    solution: 'Interactive guides educate and include all guests'
  }, {
    problem: 'Lost Photos & Videos',
    solution: 'Centralized, AI-tagged memory collection'
  }, {
    problem: 'Logistics Chaos',
    solution: 'Smart coordination and real-time updates'
  }];
  const stats = [{
    value: '40,000+',
    label: 'Guests Connected'
  }, {
    value: '100,000+',
    label: 'Memories Shared'
  }, {
    value: '98%',
    label: 'Satisfaction Rate'
  }];
  return <div className="bg-gradient-to-b from-purple-50 via-white to-pink-50" data-id="element-838">
      {/* Decorative Pattern Overlay - More Festive */}
      <div className="fixed inset-0 pointer-events-none opacity-5 z-0" data-id="element-839">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-840">
          <defs data-id="element-841">
            <pattern id="paisley" x="0" y="0" width="150" height="150" patternUnits="userSpaceOnUse" data-id="element-842">
              <path d="M75 10 Q 90 30, 85 50 Q 80 70, 75 75 Q 70 70, 65 50 Q 60 30, 75 10 Z" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-843" />
              <circle cx="75" cy="45" r="8" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-844" />
              <path d="M75 20 L75 30 M70 25 L80 25" stroke="currentColor" strokeWidth="1" data-id="element-845" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#paisley)" className="text-purple-600" data-id="element-846" />
        </svg>
      </div>

      {/* Hero Section - Full Bleed Image */}
      <section className="relative min-h-screen flex items-center overflow-hidden -mt-20" data-id="element-847">
        {/* Background Image - Brighter, Less Dulled */}
        <div className="absolute inset-0" data-id="element-848">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dBJQSqi7ZjqK7ceqZsgzaN/Hero1.webp" alt="South Asian Wedding Celebration" className="w-full h-full object-cover object-top" data-id="element-849" />
          {/* Lighter Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/20 to-transparent" data-id="element-850"></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 md:px-6 relative z-10 pt-20" data-id="element-851">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-852">
            {/* Left side - Empty for image visibility */}
            <div className="hidden lg:block" data-id="element-853"></div>

            {/* Right side - Text Content */}
            <div className="text-white" data-id="element-854">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-lg" data-id="element-855">
                Turn your wedding into a living, breathing digital festival.
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed drop-shadow-md" data-id="element-856">
                DestaPass is your private wedding circle—where both sides of the
                family can connect, play, and celebrate together long before
                they meet in person.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-857">
                <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" data-id="element-858">
                  <Button variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 border-0 shadow-xl font-semibold" data-id="element-859">
                    Book a Demo for Your Wedding
                    <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-860" />
                  </Button>
                </a>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm drop-shadow-md" data-id="element-861">
                <div className="flex items-center" data-id="element-862">
                  <CheckIcon className="h-5 w-5 text-green-400 mr-2" data-id="element-863" />
                  <span data-id="element-864">Included in select DestaWed packages</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce" data-id="element-865">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-866">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" data-id="element-867" />
          </svg>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600" data-id="element-868">
        <div className="container mx-auto px-4 md:px-6" data-id="element-869">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-870">
            {stats.map((stat, index) => <div key={index} className="text-center" data-id="element-871">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2" data-id="element-872">
                  {stat.value}
                </div>
                <div className="text-purple-100" data-id="element-873">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Problem/Solution - Redesigned */}
      <section className="py-16 md:py-24 bg-white" data-id="element-874">
        <div className="container mx-auto px-4 md:px-6" data-id="element-875">
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-876">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-877">
              Traditional Weddings,{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-878">
                Modern Solutions
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-879">
              We understand the unique challenges of South Asian weddings.
              DestaPass solves them elegantly.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-880">
            {problems.map((item, index) => <Card key={index} className="border-2 border-gray-100 hover:border-purple-200 transition-colors overflow-hidden" data-id="element-881">
                <CardBody className="p-0" data-id="element-882">
                  {/* Problem Section - Red Background */}
                  <div className="bg-red-50 p-6 border-b-2 border-gray-100" data-id="element-883">
                    <div className="flex items-center gap-3" data-id="element-884">
                      <div className="flex-shrink-0 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center" data-id="element-885">
                        <XIcon className="h-4 w-4 text-white stroke-[3]" data-id="element-886" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-lg" data-id="element-887">
                        {item.problem}
                      </h3>
                    </div>
                  </div>
                  {/* Solution Section - Green Background */}
                  <div className="bg-green-50 p-6" data-id="element-888">
                    <div className="flex items-center gap-3" data-id="element-889">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center" data-id="element-890">
                        <CheckIcon className="h-4 w-4 text-white stroke-[3]" data-id="element-891" />
                      </div>
                      <p className="text-gray-700 font-medium" data-id="element-892">
                        {item.solution}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Features Grid - Better Top Padding */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50 to-pink-50" data-id="element-893">
        <div className="container mx-auto px-4 md:px-6" data-id="element-894">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-895">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-896">
              Everything You Need,
              <br data-id="element-897" />
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-898">
                All in One Place
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-899">
              Seven powerful features that transform your wedding experience
              from start to finish.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-900">
            {features.map((feature, index) => <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white" data-id="element-901">
                <CardBody className="p-8 pt-10" data-id="element-902">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center text-white mb-8 shadow-lg`} data-id="element-903">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-904">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-905">
                    {feature.description}
                  </p>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Timeline - Better Top Padding */}
      <section className="py-16 md:py-24 bg-white" data-id="element-906">
        <div className="container mx-auto px-4 md:px-6" data-id="element-907">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-908">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-909">
              Your{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-910">
                Wedding Journey
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-911">
              From the moment you get engaged to years after, DestaPass is with
              you every step of the way.
            </p>
          </div>
          <div className="max-w-5xl mx-auto" data-id="element-912">
            <div className="relative" data-id="element-913">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-200 via-pink-200 to-purple-200 transform -translate-x-1/2" data-id="element-914"></div>

              <div className="space-y-12" data-id="element-915">
                {timeline.map((phase, index) => <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-916">
                    <div className="flex-1 md:pr-12" data-id="element-917">
                      <Card className={`border-2 border-purple-200 shadow-lg ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'} max-w-md`} data-id="element-918">
                        <CardBody className="p-8 pt-10" data-id="element-919">
                          <div className="flex items-center gap-3 mb-6" data-id="element-920">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white" data-id="element-921">
                              {phase.icon}
                            </div>
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-922">
                              {phase.phase}
                            </h3>
                          </div>
                          <ul className="space-y-2" data-id="element-923">
                            {phase.activities.map((activity, i) => <li key={i} className="flex items-start gap-2 text-gray-600" data-id="element-924">
                                <CheckIcon className="h-5 w-5 text-purple-500 flex-shrink-0 mt-0.5" data-id="element-925" />
                                <span data-id="element-926">{activity}</span>
                              </li>)}
                          </ul>
                        </CardBody>
                      </Card>
                    </div>
                    {/* Timeline node */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold shadow-lg z-10" data-id="element-927">
                      {index + 1}
                    </div>
                    <div className="flex-1 md:pl-12" data-id="element-928"></div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Couples vs Guests Tabs */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50 to-pink-50" data-id="element-929">
        <div className="container mx-auto px-4 md:px-6" data-id="element-930">
          <div className="max-w-5xl mx-auto" data-id="element-931">
            {/* Tab Buttons */}
            <div className="flex justify-center mb-12" data-id="element-932">
              <div className="inline-flex bg-white rounded-full p-2 shadow-lg" data-id="element-933">
                <button onClick={() => setActiveTab('couples')} className={`px-8 py-3 rounded-full font-semibold transition-all ${activeTab === 'couples' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-934">
                  For Couples
                </button>
                <button onClick={() => setActiveTab('guests')} className={`px-8 py-3 rounded-full font-semibold transition-all ${activeTab === 'guests' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-935">
                  For Guests
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'couples' ? <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-936">
                <div data-id="element-937">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6" data-id="element-938">
                    Less Stress,{' '}
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-939">
                      More Joy
                    </span>
                  </h2>
                  <p className="text-xl text-gray-600 mb-8" data-id="element-940">
                    Focus on what matters—celebrating your love. We handle the
                    digital experience so you don't have to.
                  </p>
                  <div className="space-y-4" data-id="element-941">
                    <div className="flex items-start gap-4" data-id="element-942">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-943">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-944" />
                      </div>
                      <div data-id="element-945">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-946">
                          Zero Setup Required
                        </h4>
                        <p className="text-gray-600" data-id="element-947">
                          We configure everything. You just invite your guests.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-948">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-949">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-950" />
                      </div>
                      <div data-id="element-951">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-952">
                          Reduce Planning Chaos
                        </h4>
                        <p className="text-gray-600" data-id="element-953">
                          One platform replaces dozens of WhatsApp groups and
                          email threads.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-954">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-955">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-956" />
                      </div>
                      <div data-id="element-957">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-958">
                          Include Remote Family
                        </h4>
                        <p className="text-gray-600" data-id="element-959">
                          Live social sharing means no one misses your special
                          moments.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4" data-id="element-960">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-961">
                        <CheckIcon className="h-5 w-5 text-purple-600" data-id="element-962" />
                      </div>
                      <div data-id="element-963">
                        <h4 className="font-semibold text-gray-900 mb-1" data-id="element-964">
                          Preserve Every Memory
                        </h4>
                        <p className="text-gray-600" data-id="element-965">
                          Never lose a photo or video again with centralized
                          collection.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative" data-id="element-966">
                  <div className="bg-white rounded-3xl shadow-2xl p-8" data-id="element-967">
                    <div className="text-center mb-6" data-id="element-968">
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-969">
                        <Sparkles className="h-10 w-10 text-purple-600" data-id="element-970" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-971">
                        Included with DestaMeet
                      </h3>
                      <p className="text-gray-600" data-id="element-972">
                        DestaPass comes free with all wedding packages
                      </p>
                    </div>
                    <div className="space-y-3 mb-6" data-id="element-973">
                      <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-974">
                        <span className="text-gray-600" data-id="element-975">Platform Setup</span>
                        <span className="font-semibold text-gray-900" data-id="element-976">
                          Included
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-977">
                        <span className="text-gray-600" data-id="element-978">
                          Live Social Sharing
                        </span>
                        <span className="font-semibold text-gray-900" data-id="element-979">
                          Included
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-980">
                        <span className="text-gray-600" data-id="element-981">Unlimited Guests</span>
                        <span className="font-semibold text-gray-900" data-id="element-982">
                          Included
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-3" data-id="element-983">
                        <span className="text-gray-600" data-id="element-984">Lifetime Access</span>
                        <span className="font-semibold text-gray-900" data-id="element-985">
                          Included
                        </span>
                      </div>
                    </div>
                    <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="block" data-id="element-986">
                      <Button variant="primary" className="w-full bg-gradient-to-r from-purple-600 to-pink-600 border-0" data-id="element-987">
                        Request Demo
                      </Button>
                    </a>
                  </div>
                </div>
              </div> : <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-988">
                <div className="order-2 md:order-1" data-id="element-989">
                  <div className="bg-white rounded-3xl shadow-2xl p-8" data-id="element-990">
                    <div className="text-center mb-6" data-id="element-991">
                      <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-992">
                        <UsersIcon className="h-10 w-10 text-purple-600" data-id="element-993" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-994">
                        Your VIP Experience
                      </h3>
                      <p className="text-gray-600" data-id="element-995">
                        Everything you need for an unforgettable celebration
                      </p>
                    </div>
                    <div className="space-y-4" data-id="element-996">
                      <div className="flex items-start gap-3" data-id="element-997">
                        <HeartIcon className="h-6 w-6 text-pink-500 flex-shrink-0 mt-1" data-id="element-998" />
                        <div data-id="element-999">
                          <h4 className="font-semibold text-gray-900" data-id="element-1000">
                            Connect Before You Arrive
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-1001">
                            Meet other guests and build friendships early
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-1002">
                        <GlobeIcon className="h-6 w-6 text-teal-500 flex-shrink-0 mt-1" data-id="element-1003" />
                        <div data-id="element-1004">
                          <h4 className="font-semibold text-gray-900" data-id="element-1005">
                            Learn & Explore
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-1006">
                            Destination guides and cultural education
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-1007">
                        <GiftIcon className="h-6 w-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-1008" />
                        <div data-id="element-1009">
                          <h4 className="font-semibold text-gray-900" data-id="element-1010">
                            Exclusive Perks
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-1011">
                            Discounts on tours, dining, and activities
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-1012">
                        <Camera className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" data-id="element-1013" />
                        <div data-id="element-1014">
                          <h4 className="font-semibold text-gray-900" data-id="element-1015">
                            Share Memories
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-1016">
                            Upload and access all wedding photos instantly
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="order-1 md:order-2" data-id="element-1017">
                  <h2 className="text-4xl font-bold text-gray-900 mb-6" data-id="element-1018">
                    More Than a Guest,{' '}
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-1019">
                      Part of the Story
                    </span>
                  </h2>
                  <p className="text-xl text-gray-600 mb-8" data-id="element-1020">
                    DestaPass transforms you from an attendee into an active
                    participant in this beautiful celebration.
                  </p>
                  <div className="space-y-6" data-id="element-1021">
                    <div data-id="element-1022">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1023">
                        Before the Wedding
                      </h4>
                      <p className="text-gray-600" data-id="element-1024">
                        Connect with other guests, learn about traditions, and
                        get excited together.
                      </p>
                    </div>
                    <div data-id="element-1025">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1026">
                        During Events
                      </h4>
                      <p className="text-gray-600" data-id="element-1027">
                        Stay informed with schedules, participate in games, and
                        share moments in real-time.
                      </p>
                    </div>
                    <div data-id="element-1028">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1029">
                        After the Celebration
                      </h4>
                      <p className="text-gray-600" data-id="element-1030">
                        Relive memories, download photos, and stay connected
                        with new friends.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </section>

      {/* Live Social Sharing Highlight */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-purple-900 via-pink-900 to-purple-900 text-white relative overflow-hidden" data-id="element-1031">
        <div className="absolute inset-0 opacity-10" data-id="element-1032">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1033">
            <defs data-id="element-1034">
              <pattern id="hearts" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-1035">
                <text x="50" y="50" fontSize="40" textAnchor="middle" fill="currentColor" data-id="element-1036">
                  💕
                </text>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hearts)" data-id="element-1037" />
          </svg>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1038">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1039">
            <Share2Icon className="h-16 w-16 mx-auto mb-6 text-pink-300" data-id="element-1040" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1041">
              Distance Doesn't Mean Missing Out
            </h2>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed" data-id="element-1042">
              Your content creator and wedding attendees create an amazing live
              experience, capturing and sharing every moment as it happens.
              Remote family and friends aren't just watching—they're part of the
              celebration.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-12" data-id="element-1043">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6" data-id="element-1044">
                <div className="text-3xl font-bold mb-2" data-id="element-1045">
                  Multiple Perspectives
                </div>
                <p className="text-purple-100" data-id="element-1046">
                  Your guests capture every moment from different angles
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6" data-id="element-1047">
                <div className="text-3xl font-bold mb-2" data-id="element-1048">Real-Time Sharing</div>
                <p className="text-purple-100" data-id="element-1049">
                  Virtual guests experience moments live as they happen
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6" data-id="element-1050">
                <div className="text-3xl font-bold mb-2" data-id="element-1051">Recorded Forever</div>
                <p className="text-purple-100" data-id="element-1052">
                  Relive every ceremony and moment whenever you want
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cultural Bridge */}
      <section className="py-16 md:py-24 bg-white" data-id="element-1053">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1054">
          <div className="max-w-5xl mx-auto" data-id="element-1055">
            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1056">
              <div data-id="element-1057">
                <h2 className="text-4xl font-bold text-gray-900 mb-6" data-id="element-1058">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-1059">
                    Everyone Included,
                  </span>{' '}
                  Everyone Informed
                </h2>
                <p className="text-xl text-gray-600 mb-8" data-id="element-1060">
                  South Asian weddings are rich with meaning and tradition.
                  DestaPass helps every guest—regardless of
                  background—understand and appreciate each beautiful moment.
                </p>
                <div className="space-y-6" data-id="element-1061">
                  <div className="flex items-start gap-4" data-id="element-1062">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1063">
                      <span className="text-2xl" data-id="element-1064">📖</span>
                    </div>
                    <div data-id="element-1065">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1066">
                        Ceremony Explanations
                      </h4>
                      <p className="text-gray-600" data-id="element-1067">
                        Interactive guides explain each ritual, its
                        significance, and what to expect.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-1068">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1069">
                      <span className="text-2xl" data-id="element-1070">👗</span>
                    </div>
                    <div data-id="element-1071">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1072">
                        Dress Code Guidance
                      </h4>
                      <p className="text-gray-600" data-id="element-1073">
                        Clear guidance on what to wear for each event, with
                        cultural context.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-1074">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-1075">
                      <span className="text-2xl" data-id="element-1076">🙏</span>
                    </div>
                    <div data-id="element-1077">
                      <h4 className="font-semibold text-gray-900 mb-2" data-id="element-1078">
                        Etiquette Tips
                      </h4>
                      <p className="text-gray-600" data-id="element-1079">
                        Respectful guidance on customs, greetings, and
                        participation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-1080">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-8 shadow-xl" data-id="element-1081">
                  <div className="bg-white rounded-2xl p-6 mb-4" data-id="element-1082">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1083">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xl" data-id="element-1084">
                        💍
                      </div>
                      <h3 className="font-bold text-gray-900" data-id="element-1085">
                        Mehendi Ceremony
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1086">
                      A joyful pre-wedding celebration where intricate henna
                      designs are applied to the bride's hands and feet,
                      symbolizing beauty, joy, and spiritual awakening.
                    </p>
                    <div className="flex items-center gap-2 text-xs text-purple-600" data-id="element-1087">
                      <span className="bg-purple-100 px-2 py-1 rounded" data-id="element-1088">
                        Duration: 3-4 hours
                      </span>
                      <span className="bg-purple-100 px-2 py-1 rounded" data-id="element-1089">
                        Attire: Colorful
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl p-6" data-id="element-1090">
                    <div className="flex items-center gap-3 mb-4" data-id="element-1091">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white text-xl" data-id="element-1092">
                        🔥
                      </div>
                      <h3 className="font-bold text-gray-900" data-id="element-1093">Sangeet Night</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1094">
                      An evening of music, dance, and celebration where both
                      families perform choreographed dances and celebrate the
                      upcoming union.
                    </p>
                    <div className="flex items-center gap-2 text-xs text-amber-600" data-id="element-1095">
                      <span className="bg-amber-100 px-2 py-1 rounded" data-id="element-1096">
                        Duration: Evening
                      </span>
                      <span className="bg-amber-100 px-2 py-1 rounded" data-id="element-1097">
                        Attire: Festive
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50 to-pink-50" data-id="element-1098">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1099">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1100">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-1101">
              Loved by{' '}
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-id="element-1102">
                Couples & Guests
              </span>
            </h2>
            <p className="text-xl text-gray-600" data-id="element-1103">
              Real stories from real weddings transformed by DestaPass.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1104">
            <Card className="border-0 shadow-xl bg-white" data-id="element-1105">
              <CardBody className="p-8" data-id="element-1106">
                <div className="flex mb-4" data-id="element-1107">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-amber-400 fill-current" data-id="element-1108" />)}
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed" data-id="element-1109">
                  "DestaPass completely transformed our wedding experience. My
                  grandmother in India was able to watch every ceremony live,
                  and she cried tears of joy. The cultural guides helped our
                  American friends understand and appreciate each tradition. It
                  was magical."
                </p>
                <div className="flex items-center gap-4" data-id="element-1110">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full" data-id="element-1111"></div>
                  <div data-id="element-1112">
                    <div className="font-semibold text-gray-900" data-id="element-1113">
                      Priya & Arjun
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-1114">
                      Married in Cancun, March 2024
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
            <Card className="border-0 shadow-xl bg-white" data-id="element-1115">
              <CardBody className="p-8" data-id="element-1116">
                <div className="flex mb-4" data-id="element-1117">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-5 w-5 text-amber-400 fill-current" data-id="element-1118" />)}
                </div>
                <p className="text-gray-600 mb-6 text-lg leading-relaxed" data-id="element-1119">
                  "As a guest, I loved connecting with other attendees before
                  the wedding. By the time we arrived in Mexico, we already had
                  a group of friends. The destination guides and activity
                  discounts made the whole trip so much more enjoyable. Best
                  wedding I've ever attended!"
                </p>
                <div className="flex items-center gap-4" data-id="element-1120">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full" data-id="element-1121"></div>
                  <div data-id="element-1122">
                    <div className="font-semibold text-gray-900" data-id="element-1123">Sarah M.</div>
                    <div className="text-sm text-gray-600" data-id="element-1124">
                      Wedding Guest, February 2024
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white relative overflow-hidden" data-id="element-1125">
        <div className="absolute inset-0 opacity-10" data-id="element-1126">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-1127">
            <defs data-id="element-1128">
              <pattern id="celebration" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-1129">
                <text x="25" y="50" fontSize="30" fill="currentColor" data-id="element-1130">
                  ✨
                </text>
                <text x="75" y="50" fontSize="30" fill="currentColor" data-id="element-1131">
                  💕
                </text>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#celebration)" data-id="element-1132" />
          </svg>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1133">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1134">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1135">
              Ready to Reimagine Your Wedding?
            </h2>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed" data-id="element-1136">
              See DestaPass in action. Book a personalized demo and discover how
              we can transform your celebration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1137">
              <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" data-id="element-1138">
                <Button variant="primary" size="lg" className="bg-white text-purple-600 hover:bg-gray-50 border-0 shadow-lg font-semibold" data-id="element-1139">
                  Request Demo
                  <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1140" />
                </Button>
              </a>
              <a href="#" className="inline-block" data-id="element-1141">
                <Button variant="outline" size="lg" className="bg-purple-700 border-2 border-white text-white hover:bg-purple-800 transition-colors font-semibold" data-id="element-1142">
                  Download Info Packet
                </Button>
              </a>
            </div>
            <p className="mt-6 text-purple-200 text-sm" data-id="element-1143">
              Included free with select DestaWed wedding packages
            </p>
          </div>
        </div>
      </section>
    </div>;
}